import React, { useState, useMemo, useCallback } from "react"
import { Select, Checkbox, Input, Tag } from "antd"
import { SearchOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"

function MultiSelectOption({ checked, label, status }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <Checkbox checked={checked} style={{ marginRight: 10, pointerEvents: "none" }}>
        {label}
      </Checkbox>
      {status && status !== "active" && (
        <Tag color="default" bordered style={{ margin: 0 }}>
          Inactive
        </Tag>
      )}
    </div>
  )
}

function DropdownWithSearch({ menu, searchPlaceholder, searchValue, onSearchChange }) {
  return (
    <>
      <div style={{ padding: "8px 12px", borderBottom: "1px solid #f0f0f0" }}>
        <Input
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          allowClear
          style={{ width: "100%" }}
        />
      </div>
      {menu}
    </>
  )
}

export default function MultiSelectWithSearch({
  value = [],
  onChange,
  options = [],
  placeholder,
  dropdownClassName,
  style,
  t,
  searchPlaceholder = "Search..."
}) {
  const [searchValue, setSearchValue] = useState("")
  const safeValue = value || []
  const safeOptions = options || []

  // Filter options based on search value
  // IMPORTANT: Always include already-selected items to prevent them from being removed
  const filteredOptions = useMemo(() => {
    if (!searchValue) return safeOptions

    return safeOptions.filter((option) => {
      // Always show "Select All" option
      if (option.value === "all") return true

      // Always show already-selected items (even if they don't match search)
      if (safeValue.includes(option.value)) return true

      // Show items that match the search
      const label = option.label?.toLowerCase() || ""
      const search = searchValue.toLowerCase()
      return label.includes(search)
    })
  }, [safeOptions, searchValue, safeValue])

  const renderOption = (option) => {
    const fullOption = safeOptions.find((o) => o.value === option.value) || option
    let checked
    if (option.value === "all") {
      const allNonAll = safeOptions.filter((o) => o.value !== "all")
      checked = allNonAll.length > 0 && allNonAll.every((o) => safeValue.includes(o.value))
    } else {
      checked = safeValue.includes(option.value)
    }
    return <MultiSelectOption checked={checked} label={option.label} status={fullOption.status} />
  }

  const dropdownRender = useCallback(
    (menu) => (
      <DropdownWithSearch menu={menu} searchPlaceholder={searchPlaceholder} searchValue={searchValue} onSearchChange={setSearchValue} />
    ),
    [searchPlaceholder, searchValue]
  )

  const selectedNonAll = safeValue.filter((v) => v !== "all")
  const totalNonAll = safeOptions.filter((o) => o.value !== "all")
  let displayLabel = null
  if (selectedNonAll.length > 0) {
    if (selectedNonAll.length === totalNonAll.length) {
      displayLabel = `${placeholder} (All)`
    } else {
      displayLabel = `${placeholder} (${selectedNonAll.length})`
    }
  }

  return (
    <div style={{ position: "relative", minWidth: 180, maxWidth: 220, ...style }}>
      <Select
        mode="multiple"
        allowClear
        showArrow
        showSearch={false}
        placeholder={placeholder}
        value={safeValue}
        filterOption={false}
        dropdownRender={dropdownRender}
        // onChange still needed for allowClear (clears all selections)
        onChange={(vals) => {
          if (!vals.length) onChange([])
        }}
        onSelect={(val) => {
          const allNonAllOptionsValues = safeOptions.filter((o) => o.value !== "all").map((o) => o.value)
          const filteredNonAllValues = filteredOptions.filter((o) => o.value !== "all").map((o) => o.value)

          if (val === "all") {
            // Selecting 'all' adds all filtered items to existing selection
            const set = new Set(safeValue.filter((v) => v !== "all"))
            filteredNonAllValues.forEach((v) => set.add(v))
            const current = Array.from(set)

            // If now ALL options are selected, add 'all' sentinel
            const allSelected = allNonAllOptionsValues.every((v) => set.has(v))
            const result = allSelected ? ["all", ...allNonAllOptionsValues] : current
            onChange(result)
            return
          }

          // Add selected value (remove sentinel 'all' first if present)
          const set = new Set(safeValue.filter((v) => v !== "all"))
          set.add(val)
          const current = Array.from(set)

          // If all options are now selected, add 'all' sentinel
          const allSelected = allNonAllOptionsValues.every((v) => set.has(v))
          const result = allSelected ? ["all", ...allNonAllOptionsValues] : current
          onChange(result)
        }}
        onDeselect={(val) => {
          if (val === "all") {
            // Deselecting 'all' clears everything
            onChange([])
            return
          }
          // Remove val and also remove 'all' sentinel if present
          const remaining = safeValue.filter((v) => v !== val && v !== "all")
          onChange(remaining)
        }}
        options={filteredOptions}
        className={dropdownClassName}
        style={{ minWidth: 180, maxWidth: 220, ...style }}
        optionLabelProp="label"
        dropdownStyle={{ borderRadius: 12, minWidth: 220 }}
        maxTagCount={0}
        tagRender={() => null}
        optionRender={renderOption}
        onDropdownVisibleChange={(open) => {
          // Clear search when dropdown closes
          if (!open) {
            setSearchValue("")
          }
        }}
      />
      {displayLabel && (
        <div
          style={{
            position: "absolute",
            left: 12,
            top: 0,
            height: "100%",
            display: "flex",
            alignItems: "center",
            pointerEvents: "none",
            fontSize: 15,
            background: "transparent"
          }}
        >
          {displayLabel}
        </div>
      )}
    </div>
  )
}

MultiSelectWithSearch.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired
    })
  ).isRequired,
  placeholder: PropTypes.string,
  dropdownClassName: PropTypes.string,
  style: PropTypes.object,
  t: PropTypes.func,
  searchPlaceholder: PropTypes.string
}

MultiSelectOption.propTypes = {
  checked: PropTypes.bool.isRequired,
  label: PropTypes.node.isRequired,
  status: PropTypes.string
}

DropdownWithSearch.propTypes = {
  menu: PropTypes.node.isRequired,
  searchPlaceholder: PropTypes.string.isRequired,
  searchValue: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired
}
